import { useState, useEffect, useRef, useCallback } from 'react';
import { apiClient } from '../api/client.js';

const POLL_INTERVAL_MS = 30000; // 30 seconds when tab visible

/**
 * Adaptive polling hook for notification unread count.
 * - Polls every 30s when tab visible
 * - Pauses when tab is hidden (Visibility API)
 * - Immediately polls on tab focus/visibility regain
 */
export function useNotificationPolling(): {
  unreadCount: number;
  refresh: () => Promise<void>;
} {
  const [unreadCount, setUnreadCount] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const data = (await apiClient.get('/api/notifications/unread-count')) as
        | { count?: number }
        | null
        | undefined;
      if (mountedRef.current) {
        setUnreadCount(data?.count ?? 0);
      }
    } catch {
      // Silently fail — polling errors should not surface to users
    }
  }, []);

  const startPolling = useCallback(() => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
  }, [fetchUnreadCount]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    // Initial fetch
    fetchUnreadCount();

    // Start polling if tab is visible
    if (!document.hidden) {
      startPolling();
    }

    function handleVisibilityChange() {
      if (document.hidden) {
        stopPolling();
      } else {
        // Immediately poll when tab becomes visible again
        fetchUnreadCount();
        startPolling();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mountedRef.current = false;
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchUnreadCount, startPolling, stopPolling]);

  return { unreadCount, refresh: fetchUnreadCount };
}

export default useNotificationPolling;
